import { BlendShapeMapper } from "../BlendShapeMapper.js";
import { AudioAnalyzer } from "./AudioAnalyzer.js";

export class LipSyncController {
    constructor(blendShapeControls) {
        this.blendShapeControls = blendShapeControls;
        this.blendShapeMapper = new BlendShapeMapper();
        // Field reserved for the A2F emotion renderer (see
        // RealtimeLLMToElevenLabs.enableLipSync). Set when a TTS session
        // begins; debug UIs read it to access manual-override APIs.
        this.a2fEmotionRenderer = null;
        this.isAnimating = false;
        this.currentTone = "neutral";
        this.initializeAudioContext();

        // Initialize the audio analyzer for detecting pauses in speech
        this.audioAnalyzer = new AudioAnalyzer();
        this.usingAudioBasedPauses = true; // Flag to track whether we're using audio-based pause detection
        this.currentAudio = null; // Current audio element being played
        this.pauseUpdateInterval = null; // Interval for checking audio position against detected pauses
        this.detectedPauses = []; // Array of detected pauses in the current audio
        this.lastAppliedPauseId = null; // ID of the last pause that was applied
        this.currentlyInPause = false; // Whether we're currently in a pause

        // Timing configuration for lip sync
        this.phonemeOffsetMs = 0; // Initial delay before starting lip movement
        this.phonemeSpeedMs = 135; // Default time between phonemes in ms (matching normal speech rate) 135
        this.endDelayMs = 0; // Delay before stopping animation after last phoneme

        // ElevenLabs timing-based configuration
        this.transitionDuration = 35; // Reduced transition duration for more responsive sync
        this.currentPhonemeSequence = []; // Will hold the phoneme sequence with actual ElevenLabs timing
        this.lastPhonemeTime = 0; // Track when the last phoneme will be processed

        // Anticipation settings for lip sync
        this.anticipation = {
            enabled: false, // Whether anticipation is enabled
            factor: 0.1, // How much to blend with the next phoneme (0-1)
            offsetFactor: 0.1, // When to start anticipating the next phoneme (0-1, relative to phonemeSpeedMs)
        };

        this.emotionIntensityRange = {
            min: 0.7, // Minimum intensity during speech
            max: 1.0, // Maximum intensity during speech
        };

        // Gradual intensity ramp configuration
        this.gradualIntensity = {
            minIntensity: 0.7, // 50% minimum (blend weight 50)
            maxIntensity: 0.7, // 80% maximum (blend weight 80)
            enabled: true, // Enable gradual intensity ramping
            rampSteps: 15, // Number of steps to reach target intensity
            updateInterval: 5, // Milliseconds between intensity updates
        };

        // Active phoneme tracking for gradual intensity
        this.activePhonemes = new Map(); // Track currently animating phonemes

        // Phoneme-specific duration multipliers
        this.phonemeDurations = {
            // Vowels (longer durations)
            AAA: 1, // Multiplier for base duration
            EH: 1,
            //'AHH': 1,
            IEE: 1,
            OHH: 1,
            UUU: 1,

            // Consonants (shorter durations)
            SSS: 1,
            FFF: 1,
            TTH: 1,
            MBP: 1,
            RRR: 1,
            WWW: 1,
            SSH: 1,
            TLDN: 1,
            //'SCHWA': 1.0,  // Default/neutral sound

            // Pauses (different durations for natural speech rhythm)
            PAUSE_SHORT: 1.0,
            PAUSE_MED: 1.0,
            PAUSE_LONG: 1.0,
        };

        // Initialize with empty blend shapes
        this.currentBlendShapes = new Map();
        this.targetBlendShapes = new Map();

        // Initialize blend shapes after setup
        this.initializeBlendShapes();

        // API endpoint for phonetic analysis
        this.phoneticAnalysisEndpoint = "/api/phonetic-analysis";

        // Cache for phonetic analysis results
        this.phoneticCache = new Map();

        // Setup UI controls if debug panel exists
        this.setupUI();
    }

    initializeAudioContext() {
        try {
            this.audioContext = new (window.AudioContext ||
                window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
            console.log("Audio context initialized successfully");
        } catch (error) {
            console.error("Failed to initialize audio context:", error);
            this.audioAvailable = false;
        }
    }

    initializeBlendShapes() {
        // Get available blend shapes from controls
        const availableBlendShapes = Object.keys(this.blendShapeControls);
        console.log("Available blend shapes:", availableBlendShapes);

        // Initialize all non-emotion blend shapes to their default values
        Object.entries(this.blendShapeControls).forEach(([name, control]) => {
            if (
                control &&
                control.mesh &&
                typeof control.index === "number" &&
                !this.blendShapeMapper.isProtectedEmotionShape(name)
            ) {
                this.currentBlendShapes.set(name, 0);
                this.targetBlendShapes.set(name, 0);
                control.mesh.morphTargetInfluences[control.index] = 0;
                control.value = 0;
            }
        });
    }

    setTone(tone) {
        if (
            ["happy", "sad", "angry", "surprise", "fear", "neutral"].includes(
                tone,
            )
        ) {
            this.currentTone = tone;
            console.log("Set lip sync tone to:", tone);
        }
    }

    /**
     * Configure anticipation settings for lip sync
     * @param {Object} settings - Settings to update
     * @param {Boolean} settings.enabled - Enable/disable anticipation
     * @param {Number} settings.factor - Blend factor between phonemes (0-1)
     * @param {Number} settings.offsetFactor - When to start anticipating (0-1, relative to phonemeSpeedMs)
     */
    setAnticipation(settings = {}) {
        if (typeof settings.enabled === "boolean") {
            this.anticipation.enabled = settings.enabled;
        }

        if (typeof settings.factor === "number") {
            this.anticipation.factor = Math.max(
                0,
                Math.min(1, settings.factor),
            );
        }

        if (typeof settings.offsetFactor === "number") {
            this.anticipation.offsetFactor = Math.max(
                0,
                Math.min(1, settings.offsetFactor),
            );
        }

        console.log(
            "Updated lip sync anticipation settings:",
            this.anticipation,
        );
    }

    /**
     * Configure gradual intensity settings for lip sync
     * @param {Object} settings - Settings to update
     * @param {Boolean} settings.enabled - Enable/disable gradual intensity
     * @param {Number} settings.rampSteps - Number of steps to reach target intensity
     * @param {Number} settings.updateInterval - Milliseconds between intensity updates
     * @param {Number} settings.minIntensity - Minimum intensity (0-1)
     * @param {Number} settings.maxIntensity - Maximum intensity (0-1)
     */
    setGradualIntensity(settings = {}) {
        if (typeof settings.enabled === "boolean") {
            this.gradualIntensity.enabled = settings.enabled;
        }

        if (typeof settings.rampSteps === "number") {
            this.gradualIntensity.rampSteps = Math.max(
                1,
                Math.min(100, settings.rampSteps),
            );
        }

        if (typeof settings.updateInterval === "number") {
            this.gradualIntensity.updateInterval = Math.max(
                5,
                Math.min(50, settings.updateInterval),
            );
        }

        if (typeof settings.minIntensity === "number") {
            this.emotionIntensityRange.min = Math.max(
                0,
                Math.min(1, settings.minIntensity),
            );
        }

        if (typeof settings.maxIntensity === "number") {
            this.emotionIntensityRange.max = Math.max(
                0,
                Math.min(1, settings.maxIntensity),
            );
        }

        console.log("Updated gradual intensity settings:", {
            gradualIntensity: this.gradualIntensity,
            intensityRange: this.emotionIntensityRange,
        });
    }

    /**
     * Process lip sync using ElevenLabs phoneme and duration arrays
     * @param {Array} phonemeSequence - Array of phonemes ['AHH', 'TTH', 'OHH', ...]
     * @param {Array} durationSequence - Array of durations in milliseconds [100, 62, 150, ...]
     * @param {HTMLAudioElement} audioElement - Audio element for synchronization
     */
    async processElevenLabsSequence(
        phonemeSequence,
        durationSequence,
        audioElement = null,
    ) {
        if (
            !phonemeSequence ||
            !durationSequence ||
            phonemeSequence.length !== durationSequence.length
        ) {
            console.error("Invalid phoneme or duration sequences provided");
            return;
        }

        console.log(
            `Starting ElevenLabs lip sync with ${phonemeSequence.length} phonemes`,
        );

        this.isAnimating = true;
        this.currentAudio = audioElement;
        this.startTime = performance.now(); // Set start time for elapsed time calculation

        // Calculate scaling factor to match actual audio duration
        const elevenLabsTotalMs = durationSequence.reduce(
            (sum, duration) => sum + duration,
            0,
        );
        let scalingFactor = 1.0;
        let actualAudioMs = elevenLabsTotalMs;

        // Wait for audio metadata to load if needed
        if (audioElement) {
            if (!audioElement.duration || audioElement.duration <= 0) {
                console.log("Audio metadata not loaded yet, waiting...");
                await new Promise((resolve) => {
                    let attempts = 0;
                    const maxAttempts = 40; // Wait up to 2 seconds
                    const checkMetadata = () => {
                        attempts++;
                        if (
                            audioElement.duration &&
                            audioElement.duration > 0
                        ) {
                            console.log(
                                `Audio metadata loaded: ${audioElement.duration}s after ${attempts} attempts`,
                            );
                            resolve();
                        } else if (attempts >= maxAttempts) {
                            console.warn(
                                "Audio metadata loading timeout, proceeding with ElevenLabs timing",
                            );
                            resolve();
                        } else {
                            setTimeout(checkMetadata, 50);
                        }
                    };
                    checkMetadata();
                });
            }

            actualAudioMs = audioElement.duration * 1000;
            const timeDifference = actualAudioMs - elevenLabsTotalMs;

            console.log(`=== TIMING ANALYSIS ===`);
            console.log(
                `Audio duration: ${Math.round(actualAudioMs)}ms (${audioElement.duration.toFixed(2)}s)`,
            );
            console.log(`ElevenLabs total: ${Math.round(elevenLabsTotalMs)}ms`);
            console.log(`Time difference: ${Math.round(timeDifference)}ms`);
            console.log(`Total phonemes: ${durationSequence.length}`);
            console.log(`=======================`);

            // Distribute the time difference proportionally across all phonemes
            if (Math.abs(timeDifference) > 100) {
                // Only adjust if difference is significant (>100ms)
                const adjustmentPerPhoneme =
                    timeDifference / durationSequence.length;
                console.log(
                    `Distributing ${Math.round(timeDifference)}ms across ${durationSequence.length} phonemes`,
                );
                console.log(
                    `Adjustment per phoneme: ${adjustmentPerPhoneme.toFixed(2)}ms`,
                );

                // Apply adjustment to each phoneme duration
                for (let i = 0; i < durationSequence.length; i++) {
                    const originalDuration = durationSequence[i];
                    const adjustedDuration = Math.max(
                        10,
                        originalDuration + adjustmentPerPhoneme,
                    ); // Minimum 10ms
                    durationSequence[i] = adjustedDuration;
                }

                // Recalculate total after adjustment
                const adjustedTotal = durationSequence.reduce(
                    (sum, duration) => sum + duration,
                    0,
                );
                actualAudioMs = adjustedTotal;
                scalingFactor = 1.0; // No additional scaling needed

                console.log(`After distributing difference:`);
                console.log(`New total: ${Math.round(adjustedTotal)}ms`);
                console.log(
                    `Final difference: ${Math.round(adjustedTotal - audioElement.duration * 1000)}ms`,
                );
                console.log(`Using direct timing (no scaling factor)`);
            } else {
                scalingFactor = actualAudioMs / elevenLabsTotalMs;
                console.log(
                    `Small difference, using scaling factor: ${scalingFactor.toFixed(3)}`,
                );
            }

            // Validate scaling factor
            if (scalingFactor < 0.1 || scalingFactor > 10) {
                console.warn(
                    `Unusual scaling factor ${scalingFactor.toFixed(3)}, using fallback approach`,
                );
                // Use a more conservative approach - extend timing to match audio
                actualAudioMs = Math.max(actualAudioMs, elevenLabsTotalMs);
                scalingFactor = actualAudioMs / elevenLabsTotalMs;
                console.log(
                    `Adjusted scaling factor: ${scalingFactor.toFixed(3)}`,
                );
            }
        } else {
            console.log(
                `Using ElevenLabs timing: ${Math.round(elevenLabsTotalMs)}ms (no audio element available)`,
            );
        }

        // Scale the durations to match actual audio
        const scaledDurations = durationSequence.map(
            (duration) => duration * scalingFactor,
        );

        let currentTime = 0;

        // SYNCHRONIZED START: All components verified ready, start together
        console.log("=== SYNCHRONIZED COMPONENT START ===");
        console.log(
            "All components verified ready - starting phonemes with precise timing",
        );

        // Process each phoneme with scaled duration - synchronized timing
        for (let i = 0; i < phonemeSequence.length; i++) {
            const phoneme = phonemeSequence[i];
            const scaledDuration = scaledDurations[i];

            // Schedule phoneme application with verified timing
            setTimeout(() => {
                if (this.isAnimating) {
                    console.log(
                        `Synchronized phoneme ${i + 1}/${phonemeSequence.length}: ${phoneme} at ${Math.round(currentTime)}ms`,
                    );
                    this.applyPhonemeWithGradualIntensity(
                        phoneme,
                        scaledDuration,
                    );
                }
            }, currentTime);

            // Update time for next phoneme using scaled duration
            currentTime += scaledDuration;
        }

        console.log(
            "All phonemes scheduled with synchronized timing coordination",
        );

        // Track when animation should end (using actual audio duration)
        this.lastPhonemeTime = Math.round(actualAudioMs);

        // Schedule animation stop to match audio end
        setTimeout(() => {
            if (this.isAnimating) {
                const currentTime = performance.now() - this.startTime;
                console.log(`=== LIP SYNC END TIMING ===`);
                console.log(
                    `Lip sync completed after: ${Math.round(actualAudioMs)}ms (scheduled)`,
                );
                console.log(
                    `Actual elapsed time: ${Math.round(currentTime)}ms`,
                );
                if (audioElement && audioElement.currentTime) {
                    console.log(
                        `Audio current time: ${Math.round(audioElement.currentTime * 1000)}ms`,
                    );
                    console.log(
                        `Audio remaining: ${Math.round((audioElement.duration - audioElement.currentTime) * 1000)}ms`,
                    );
                }
                console.log(`========================`);
                this.stopAnimation();
            }
        }, this.lastPhonemeTime);

        console.log(
            `ElevenLabs lip sync will run for ${Math.round(actualAudioMs)}ms to match audio duration`,
        );

        // Start the animation loop
        this.animate();

        // Return the actual audio duration
        return this.lastPhonemeTime;
    }

    async prepareElevenLabsData(requestId, audioElement = null) {
        /**
         * Pre-fetch and prepare ElevenLabs data without starting animation
         */
        try {
            console.log("Preparing ElevenLabs data for request:", requestId);
            console.log(
                "Audio element for preparation:",
                audioElement ? "Available" : "Missing",
            );

            // Get the processed phoneme sequence from ElevenLabs timing data
            const sequenceData = await this.getElevenLabsSequence(requestId);

            if (
                !sequenceData.phonemeSequence ||
                sequenceData.phonemeSequence.length === 0
            ) {
                throw new Error(
                    "No phoneme sequence available from ElevenLabs data",
                );
            }

            // Store the prepared data for immediate use when audio starts
            this.preparedElevenLabsData = {
                phonemeSequence: sequenceData.phonemeSequence,
                durationSequence: sequenceData.durationSequence,
                totalDuration: sequenceData.totalDuration,
                requestId: requestId,
                audioElement: audioElement, // Store the audio element for later use
            };

            console.log(
                `ElevenLabs data prepared: ${sequenceData.phonemeSequence.length} phonemes, ${sequenceData.totalDuration}ms total`,
            );
            return true;
        } catch (error) {
            console.error("Error preparing ElevenLabs data:", error);
            this.preparedElevenLabsData = null;
            throw error;
        }
    }

    startPreparedElevenLabsAnimation(audioElement = null) {
        /**
         * Start lip sync animation with pre-prepared ElevenLabs data for perfect synchronization
         */
        if (!this.preparedElevenLabsData) {
            throw new Error(
                "No prepared ElevenLabs data available - call prepareElevenLabsData first",
            );
        }

        if (this.isAnimating) {
            this.stopAnimation();
        }

        try {
            console.log("Starting synchronized ElevenLabs lip sync animation");

            // Use the stored audio element if current one is not provided
            const finalAudioElement =
                audioElement || this.preparedElevenLabsData.audioElement;
            console.log(
                "Audio element available for timing:",
                finalAudioElement ? "Yes" : "No",
            );
            console.log(
                "Audio element source:",
                audioElement
                    ? "Parameter"
                    : this.preparedElevenLabsData.audioElement
                      ? "Stored"
                      : "None",
            );

            const { phonemeSequence, durationSequence } =
                this.preparedElevenLabsData;

            // Use the exact same timing logic as processElevenLabsSequence but data is already prepared
            return this.processElevenLabsSequence(
                phonemeSequence,
                durationSequence,
                finalAudioElement,
            );
        } catch (error) {
            console.error(
                "Error starting prepared ElevenLabs animation:",
                error,
            );
            throw error;
        } finally {
            // Clean up prepared data
            this.preparedElevenLabsData = null;
        }
    }

    async speak(text, speed = "normal") {
        if (!text) {
            console.warn("No text provided for speech");
            return;
        }

        if (this.isAnimating) {
            this.stopAnimation();
        }

        console.log(
            `Speaking with ElevenLabs timing (no artificial speed control)`,
        );

        // Prepare phoneme data first, but don't start animation yet
        try {
            // Note: This method requires a request ID from ElevenLabs processing
            // For now, we'll fall back to phonetic analysis until request ID is available
            throw new Error(
                "Request ID needed for ElevenLabs sequence - using fallback",
            );
        } catch (error) {
            console.error(
                "Error processing speech with ElevenLabs timing, falling back to phonetic analysis:",
                error,
            );

            // Fallback to old phonetic analysis method
            try {
                const phonemes = await this.getPhonemes(text);
                this.preparedPhonemes = {
                    phonemes,
                    anticipationEnabled: this.anticipation.enabled,
                    anticipationFactor: this.anticipation.factor,
                    anticipationOffset: 50, // Fixed anticipation offset for fallback mode
                    useElevenLabsTiming: false,
                };
                console.log(
                    `Prepared ${phonemes.length} phonemes using fallback phonetic analysis`,
                );
            } catch (fallbackError) {
                console.error(
                    "Error with fallback phonetic analysis, using character-based:",
                    fallbackError,
                );
                // Fall back to character-based method preparation
                this.preparePhonemes(text);
            }
        }

        // Start the animation with prepared phonemes
        this.isAnimating = true;
        this.lastPhonemeTime = 0; // Reset last phoneme time

        // Start the animation with the prepared phonemes
        const duration = this.startPreparedAnimation();
        console.log("Started speaking animation for text:", text);

        // Return the estimated duration of the lip sync animation
        return duration;
    }

    async getPhonemes(text) {
        // Check if we have a cached result
        if (this.phoneticCache.has(text)) {
            console.log("Using cached phonetic analysis for:", text);
            return this.phoneticCache.get(text);
        }

        try {
            console.log("Requesting phonetic analysis for:", text);
            const response = await fetch(this.phoneticAnalysisEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) {
                throw new Error(
                    `Phonetic analysis request failed with status: ${response.status}`,
                );
            }

            const data = await response.json();

            if (data.status !== "success") {
                throw new Error(
                    `Phonetic analysis error: ${data.error || "Unknown error"}`,
                );
            }

            // Cache the results for future use
            this.phoneticCache.set(text, data.phonemes.sequence);

            console.log("Received phonetic analysis:", data.phonemes.sequence);
            return data.phonemes.sequence;
        } catch (error) {
            console.error("Error fetching phonetic analysis:", error);
            throw error;
        }
    }

    /**
     * Get phoneme sequence and duration arrays from ElevenLabs response data
     * @param {string} requestId - Request ID to get ElevenLabs data for
     * @returns {Object} Object with phonemeSequence and durationSequence arrays
     */
    async getElevenLabsSequence(requestId) {
        // Check if we have a cached result
        const cacheKey = `elevenlabs_${requestId}`;
        if (this.phoneticCache.has(cacheKey)) {
            console.log(
                "Using cached ElevenLabs sequence for request:",
                requestId,
            );
            return this.phoneticCache.get(cacheKey);
        }

        try {
            console.log(
                "Fetching ElevenLabs lip sync data for request:",
                requestId,
            );
            const response = await fetch(
                `/api/elevenlabs-lipsync/${requestId}`,
            );

            if (!response.ok) {
                throw new Error(
                    `ElevenLabs lip sync request failed with status: ${response.status}`,
                );
            }

            const data = await response.json();

            if (data.status !== "completed") {
                throw new Error(
                    `ElevenLabs data not ready: ${data.error || "Processing incomplete"}`,
                );
            }

            // Extract phoneme sequences from the response (already mapped on backend)
            const phonemes = data.sequence
                ? data.sequence.map((item) => item.phoneme)
                : [];
            const durations = data.sequence
                ? data.sequence.map((item) => item.duration_ms || 100)
                : [];

            const sequenceData = {
                phonemeSequence: phonemes,
                durationSequence: durations,
                totalDuration: data.total_duration_ms || 0,
                wordSequence: data.word_sequence || [],
            };

            // Cache the results for future use
            this.phoneticCache.set(cacheKey, sequenceData);

            console.log(
                `Received ElevenLabs sequence: ${sequenceData.phonemeSequence.length} phonemes, ${sequenceData.totalDuration}ms total`,
            );
            console.log(
                "Phoneme sequence:",
                sequenceData.phonemeSequence.slice(0, 10),
                "...",
            );
            console.log(
                "Duration sequence:",
                sequenceData.durationSequence.slice(0, 10),
                "...",
            );

            return sequenceData;
        } catch (error) {
            console.error("Error fetching ElevenLabs sequence:", error);
            throw error;
        }
    }

    /**
     * Map raw phonemes to standard lip sync phonemes
     * @param {Array} rawPhonemes - Array of raw phonemes from ElevenLabs
     * @returns {Array} Array of mapped phonemes for lip sync
     */
    mapPhonemeSequence(rawPhonemes) {
        if (!rawPhonemes || rawPhonemes.length === 0) {
            return [];
        }

        // Phoneme mapping from CMU to standard lip sync phonemes
        const phonemeMap = {
            // Vowels
            AA: "AAA",
            AE: "AAA",
            AH: "AAA",
            AO: "OHH",
            AW: "AAA",
            AY: "AAA",
            EH: "EH",
            ER: "EH",
            EY: "EH",
            IH: "IEE",
            IY: "IEE",
            OW: "OHH",
            OY: "OHH",
            UH: "UUU",
            UW: "UUU",

            // Consonants
            B: "MBP",
            CH: "SSH",
            D: "TLDN",
            DH: "TTH",
            F: "FFF",
            G: "TLDN",
            HH: "AAA",
            JH: "SSH",
            K: "TLDN",
            L: "TLDN",
            M: "MBP",
            N: "TLDN",
            NG: "TLDN",
            P: "MBP",
            R: "RRR",
            S: "SSS",
            SH: "SSH",
            T: "TLDN",
            TH: "TTH",
            V: "FFF",
            W: "WWW",
            Y: "IEE",
            Z: "SSS",
            ZH: "SSH",

            // Special cases
            SIL: "PAUSE_SHORT",
            SP: "PAUSE_SHORT",
        };

        const mappedPhonemes = rawPhonemes.map((phoneme) => {
            if (!phoneme) return "PAUSE_SHORT";

            const upperPhoneme = phoneme.toString().toUpperCase();
            const mapped = phonemeMap[upperPhoneme];

            if (mapped) {
                return mapped;
            }

            // Default fallback for unknown phonemes
            console.warn(`Unknown phoneme: ${phoneme}, using default AAA`);
            return "AAA";
        });

        console.log(
            "Phoneme mapping:",
            rawPhonemes.slice(0, 10),
            "→",
            mappedPhonemes.slice(0, 10),
        );
        return mappedPhonemes;
    }

    /**
     * Start lip sync animation using ElevenLabs request ID
     * @param {string} requestId - Request ID from ElevenLabs processing
     * @param {HTMLAudioElement} audioElement - Audio element for synchronization
     */
    async speakWithElevenLabsData(requestId, audioElement = null) {
        if (!requestId) {
            console.error("No request ID provided for ElevenLabs lip sync");
            return;
        }

        if (this.isAnimating) {
            this.stopAnimation();
        }

        try {
            console.log(
                "Starting lip sync with ElevenLabs data for request:",
                requestId,
            );

            // Get the processed phoneme sequence from ElevenLabs timing data
            const sequenceData = await this.getElevenLabsSequence(requestId);

            if (
                !sequenceData.phonemeSequence ||
                sequenceData.phonemeSequence.length === 0
            ) {
                throw new Error(
                    "No phoneme sequence available from ElevenLabs data",
                );
            }

            // Use the ElevenLabs timing data directly
            const duration = await this.processElevenLabsSequence(
                sequenceData.phonemeSequence,
                sequenceData.durationSequence,
                audioElement,
            );

            console.log(
                `ElevenLabs lip sync started for request ${requestId}, duration: ${duration}ms`,
            );
            return duration;
        } catch (error) {
            console.error("Error starting ElevenLabs lip sync:", error);
            throw error;
        }
    }

    async processPhonemes(phonemes) {
        let currentTime = this.phonemeOffsetMs; // Start with initial offset

        // Use anticipation settings from configuration
        const anticipationEnabled = this.anticipation.enabled;
        const anticipationFactor = this.anticipation.factor;
        const anticipationOffset =
            this.phonemeSpeedMs * this.anticipation.offsetFactor;

        // Process each phoneme and schedule its application
        for (let i = 0; i < phonemes.length; i++) {
            const currentPhoneme = phonemes[i];
            const nextPhoneme =
                i < phonemes.length - 1 ? phonemes[i + 1] : null;

            // Schedule the phoneme application
            setTimeout(() => {
                this.applyPhoneme(currentPhoneme);
            }, currentTime);

            // If anticipation is enabled and we have a next phoneme, schedule anticipation
            if (anticipationEnabled && nextPhoneme) {
                setTimeout(() => {
                    this.applyAnticipatedPhoneme(
                        currentPhoneme,
                        nextPhoneme,
                        anticipationFactor,
                    );
                }, currentTime + anticipationOffset);
            }

            // Apply phoneme-specific duration
            const durationMultiplier =
                this.phonemeDurations[currentPhoneme] || 1.0;
            const phonemeDuration = this.phonemeSpeedMs * durationMultiplier;

            // Update time for next phoneme
            currentTime += phonemeDuration;

            // Update last phoneme time to track when animation should end
            this.lastPhonemeTime = currentTime;

            // Log phoneme duration for debugging
            if (i === 0 || i === phonemes.length - 1 || i % 10 === 0) {
                console.log(
                    `Phoneme ${currentPhoneme} duration: ${phonemeDuration}ms (multiplier: ${durationMultiplier})`,
                );
            }
        }

        const syncMode = anticipationEnabled
            ? "with anticipation"
            : "without anticipation";
        console.log(
            `Lip sync animation scheduled for approximately ${this.lastPhonemeTime + this.endDelayMs}ms ${syncMode}`,
        );
    }

    // Storage for prepared phonemes (used for synchronization)
    preparedPhonemes = null;

    /**
     * Prepare phoneme data for animation without starting the animation yet
     * This is part of the improved synchronization system
     * @param {string} text - The text to process for lip sync
     * @returns {Object} - Object containing prepared phoneme data
     */
    preparePhonemes(text) {
        if (!text) {
            console.warn("No text provided for phoneme preparation");
            return null;
        }

        const chars = [...text.toUpperCase()];
        let i = 0;
        let phonemes = []; // Store all phonemes

        // Extract all phonemes from text
        while (i < chars.length) {
            const phoneme = this.getPhonemeFromChar(
                chars[i],
                i < chars.length - 1 ? chars[i + 1] : null,
            );
            const skipNext = phoneme === "TTH";

            phonemes.push(phoneme);
            i += skipNext ? 2 : 1;
        }

        // Store the prepared phonemes for later use
        this.preparedPhonemes = {
            phonemes,
            anticipationEnabled: this.anticipation.enabled,
            anticipationFactor: this.anticipation.factor,
            anticipationOffset:
                this.phonemeSpeedMs * this.anticipation.offsetFactor,
        };

        console.log(
            `Prepared ${phonemes.length} phonemes for synchronized animation`,
        );
        return this.preparedPhonemes;
    }

    /**
     * Start the animation with previously prepared phonemes
     * Called exactly when audio playback begins to ensure perfect synchronization
     * @returns {number} - Estimated duration of the animation in milliseconds
     */
    startPreparedAnimation() {
        if (!this.preparedPhonemes) {
            console.warn("No prepared phonemes available to start animation");
            return 0;
        }

        if (this.isAnimating) {
            this.stopAnimation();
        }

        this.isAnimating = true;
        this.lastPhonemeTime = 0; // Reset last phoneme time
        let currentTime = 0; // Start immediately using ElevenLabs timing

        // Check if we're using ElevenLabs timing or fallback methods
        console.log("PreparedPhonemes structure:", this.preparedPhonemes);
        if (this.preparedPhonemes.useElevenLabsTiming) {
            // Use ElevenLabs phoneme sequence and duration arrays
            const { phonemeSequence, durationSequence } = this.preparedPhonemes;

            console.log(
                `Using ElevenLabs timing: ${phonemeSequence.length} phonemes with exact durations`,
            );

            // Process each phoneme with its exact duration from ElevenLabs
            for (let i = 0; i < phonemeSequence.length; i++) {
                const phoneme = phonemeSequence[i];
                const duration = durationSequence[i];

                // Schedule the phoneme application with gradual intensity
                setTimeout(() => {
                    if (this.isAnimating) {
                        this.applyPhonemeWithGradualIntensity(
                            phoneme,
                            duration,
                        );
                    }
                }, currentTime);

                // Update time for next phoneme using exact ElevenLabs duration
                currentTime += duration;
            }

            this.lastPhonemeTime = currentTime;
            console.log(
                `ElevenLabs lip sync scheduled with ${phonemeSequence.length} phonemes, total duration: ${this.lastPhonemeTime}ms`,
            );
        } else {
            // Use traditional phonetic analysis with configured timing
            const {
                phonemes,
                anticipationEnabled,
                anticipationFactor,
                anticipationOffset,
            } = this.preparedPhonemes;

            // Process the prepared phonemes with traditional timing
            for (let i = 0; i < phonemes.length; i++) {
                const currentPhoneme = phonemes[i];
                const nextPhoneme =
                    i < phonemes.length - 1 ? phonemes[i + 1] : null;

                // Apply phoneme-specific duration
                const durationMultiplier =
                    this.phonemeDurations[currentPhoneme] || 1.0;
                const phonemeDuration =
                    this.phonemeSpeedMs * durationMultiplier;

                // Schedule the phoneme application (traditional method - instant)
                setTimeout(() => {
                    this.applyPhoneme(currentPhoneme);
                }, currentTime);

                // If anticipation is enabled and we have a next phoneme, schedule anticipation
                if (anticipationEnabled && nextPhoneme) {
                    setTimeout(() => {
                        this.applyAnticipatedPhoneme(
                            currentPhoneme,
                            nextPhoneme,
                            anticipationFactor,
                        );
                    }, currentTime + anticipationOffset);
                }

                // Update time for next phoneme
                currentTime += phonemeDuration;

                // Update last phoneme time to track when animation should end
                this.lastPhonemeTime = currentTime;

                // Log phoneme duration for debugging (only for a few phonemes to avoid console spam)
                if (i === 0 || i === phonemes.length - 1 || i % 10 === 0) {
                    console.log(
                        `Synchronized phoneme ${currentPhoneme} duration: ${phonemeDuration}ms (multiplier: ${durationMultiplier})`,
                    );
                }
            }

            const syncMode = anticipationEnabled
                ? "with anticipation"
                : "without anticipation";
            console.log(
                `Synchronized lip sync animation started with ${phonemes.length} phonemes, duration: ~${this.lastPhonemeTime}ms ${syncMode}`,
            );
        }

        // Clear the prepared phonemes after starting
        this.preparedPhonemes = null;

        this.animate(); // Start the animation loop

        // Return the actual duration from ElevenLabs timing
        return this.lastPhonemeTime;
    }

    // Character-based method as fallback - with configurable anticipation
    processWordCharacterBased(text) {
        const chars = [...text.toUpperCase()];
        let i = 0;
        let currentTime = this.phonemeOffsetMs; // Start with initial offset
        let phonemes = []; // Store all phonemes first

        // Use anticipation settings from configuration
        const anticipationEnabled = this.anticipation.enabled;
        const anticipationFactor = this.anticipation.factor;
        const anticipationOffset =
            this.phonemeSpeedMs * this.anticipation.offsetFactor;

        // First, extract all phonemes from text
        while (i < chars.length) {
            const phoneme = this.getPhonemeFromChar(
                chars[i],
                i < chars.length - 1 ? chars[i + 1] : null,
            );
            const skipNext = phoneme === "TTH";

            phonemes.push(phoneme);
            i += skipNext ? 2 : 1;
        }

        // Now process phonemes with anticipation if enabled
        for (let i = 0; i < phonemes.length; i++) {
            const currentPhoneme = phonemes[i];
            const nextPhoneme =
                i < phonemes.length - 1 ? phonemes[i + 1] : null;

            // Schedule the phoneme application
            setTimeout(() => {
                this.applyPhoneme(currentPhoneme);
            }, currentTime);

            // If anticipation is enabled and we have a next phoneme, schedule anticipation
            if (anticipationEnabled && nextPhoneme) {
                setTimeout(() => {
                    this.applyAnticipatedPhoneme(
                        currentPhoneme,
                        nextPhoneme,
                        anticipationFactor,
                    );
                }, currentTime + anticipationOffset);
            }

            // Apply phoneme-specific duration
            const durationMultiplier =
                this.phonemeDurations[currentPhoneme] || 1.0;
            const phonemeDuration = this.phonemeSpeedMs * durationMultiplier;

            // Update time for next phoneme
            currentTime += phonemeDuration;

            // Update last phoneme time to track when animation should end
            this.lastPhonemeTime = currentTime;

            // Log phoneme duration for debugging (only for a few phonemes to avoid console spam)
            if (i === 0 || i === phonemes.length - 1 || i % 10 === 0) {
                console.log(
                    `Fallback phoneme ${currentPhoneme} duration: ${phonemeDuration}ms (multiplier: ${durationMultiplier})`,
                );
            }
        }

        const syncMode = anticipationEnabled
            ? "with anticipation"
            : "without anticipation";
        console.log(
            `Character-based lip sync animation scheduled for approximately ${this.lastPhonemeTime + this.endDelayMs}ms ${syncMode}`,
        );
    }

    getPhonemeFromChar(char, nextChar) {
        // Check for digraph TH first
        if (nextChar && char + nextChar === "TH") return "TTH";

        // Map characters to standard phonemes
        if ("AEIOU".includes(char)) {
            const phonemeMap = {
                A: "AAA",
                E: "EH",
                I: "IEE",
                O: "OHH",
                U: "UUU",
            };
            return phonemeMap[char];
        }

        // Map consonants
        const consonantMap = {
            F: "FFF",
            V: "FFF",
            M: "MBP",
            B: "MBP",
            P: "MBP",
            R: "RRR",
            S: "SSS",
            W: "WWW",
        };

        return consonantMap[char] || "MBP";
    }

    /**
     * Reset all blendshapes to zero instantly
     */
    resetAllBlendShapes() {
        if (this.modelViewer && this.modelViewer.blendShapeManager) {
            // Reset all blendshapes to 0
            this.targetBlendShapes.clear();
            this.currentBlendShapes.clear();
            this.modelViewer.blendShapeManager.resetAllBlendShapes();
        }
    }

    /**
     * Clear all active phoneme animations
     */
    clearAllActivePhonemes() {
        for (const [phoneme, data] of this.activePhonemes) {
            if (data.interval) {
                clearInterval(data.interval);
            }
        }
        this.activePhonemes.clear();
    }

    /**
     * More efficient: Clear active phonemes and reset only their affected blendshapes
     */
    clearActivePhonemeShapes() {
        // Track which blendshapes need to be reset
        const shapesToReset = new Set();

        for (const [phoneme, data] of this.activePhonemes) {
            // Stop the animation interval
            if (data.interval) {
                clearInterval(data.interval);
            }

            // Collect blendshapes that were affected by this phoneme
            if (data.targetShapes) {
                data.targetShapes.forEach(({ name }) => {
                    shapesToReset.add(name);
                });
            }
        }

        // Reset only the affected blendshapes to 0
        shapesToReset.forEach((shapeName) => {
            this.targetBlendShapes.set(shapeName, 0);
        });

        this.activePhonemes.clear();
    }

    /**
     * Reset only blendshapes that have been modified (non-zero values)
     */
    resetModifiedBlendShapes() {
        // Only reset blendshapes that have non-zero target values
        for (const [shapeName, value] of this.targetBlendShapes) {
            if (
                value !== 0 &&
                !this.blendShapeMapper.isProtectedEmotionShape(shapeName)
            ) {
                this.targetBlendShapes.set(shapeName, 0);
            }
        }
    }

    /**
     * Apply phoneme with a fixed intensity level (for fast phonemes under 100ms)
     * @param {string} phoneme - The phoneme to apply
     * @param {number} intensity - Fixed intensity level (0.0 to 1.0)
     */
    applyPhonemeWithFixedIntensity(phoneme, intensity) {
        // MBP phoneme always gets 0 weight - no mouth movement
        if (phoneme === "MBP") {
            console.log(
                `[MBP PHONEME] ${phoneme} - applying 0 weight (no mouth movement) regardless of requested ${(intensity * 100).toFixed(0)}%`,
            );
            this.applyPhoneme(phoneme); // Use direct application for 0 weight
            return;
        }

        // IMPORTANT: Reset previous blendshapes before applying instant intensity
        this.resetModifiedBlendShapes();
        console.log(
            `[DEBUG] Reset previous blendshapes before applying fast phoneme ${phoneme}`,
        );

        // Get target blendshape values for this phoneme
        const targetShapes = this.getPhonemeTargetShapes(phoneme);

        // Apply the fixed intensity to all target shapes immediately
        targetShapes.forEach(({ name, targetWeight }) => {
            const fixedIntensity = targetWeight * intensity;
            this.targetBlendShapes.set(name, fixedIntensity);
        });

        console.log(
            `Applied ${phoneme} with fixed ${(intensity * 100).toFixed(0)}% intensity (${targetShapes.length} shapes)`,
        );
    }

    /**
     * Apply phoneme with gradual intensity ramping over the specified duration
     * @param {string} phoneme - The phoneme to apply
     * @param {number} duration - Duration in milliseconds to ramp up to target intensity
     */
    applyPhonemeWithGradualIntensity(phoneme, duration) {
        console.log(
            `[DEBUG] applyPhonemeWithGradualIntensity called: phoneme=${phoneme}, duration=${duration}ms, enabled=${this.gradualIntensity.enabled}`,
        );

        // MBP (mouth closure) phoneme always gets 100% intensity for a clean close.
        if (phoneme === "MBP") {
            console.log(
                `[MBP PHONEME] ${phoneme} - applying instant 100% intensity (mouth closure)`,
            );
            this.applyPhoneme(phoneme); // Use direct application for 100% intensity
            return;
        }

        if (!this.gradualIntensity.enabled) {
            this.applyPhoneme(phoneme);
            return;
        }

        // --- REVISED LOGIC FOR SHORT PHONEMES ---
        const shortPhonemeThreshold = 750; // Let's use 80ms as the threshold for what's "short"
        if (duration < shortPhonemeThreshold) {
            // Scale the intensity based on the duration.
            // A 20ms phoneme will have much less intensity than a 75ms one.
            let intensity = (duration / 100) * 0.9; // Max intensity for short phonemes is 60%
            intensity = Math.min(intensity, 0.7);

            console.log(
                `[FAST PHONEME] ${phoneme} duration ${duration}ms < ${shortPhonemeThreshold}ms - applying instant ${Math.round(intensity * 100)}% intensity`,
            );
            this.applyPhonemeWithFixedIntensity(phoneme, intensity);
            return;
        }

        // --- EXISTING LOGIC FOR LONG PHONEMES ---
        // This part of your code for gradual ramping is good for longer sounds.
        this.resetModifiedBlendShapes();
        console.log(
            `[DEBUG] Reset only modified blendshapes before starting ${phoneme}`,
        );

        if (phoneme.startsWith("PAUSE_")) {
            this.applyPhoneme(phoneme);
            return;
        }

        // ... (the rest of your gradual intensity logic remains the same)
        const targetShapes = this.getPhonemeTargetShapes(phoneme);
        const updateInterval = this.gradualIntensity.updateInterval;
        const totalRampTime = Math.min(duration * 0.8, duration);
        const startTime = performance.now();

        console.log(
            `Starting gradual intensity for ${phoneme}: ${duration}ms duration...`,
        );

        const interval = setInterval(() => {
            if (!this.isAnimating) {
                clearInterval(interval);
                this.activePhonemes.delete(phoneme);
                return;
            }

            const elapsedTime = performance.now() - startTime;
            const progress = Math.min(elapsedTime / totalRampTime, 1.0);
            const easedProgress = this.easeInOutCubic(progress);

            const baseIntensity =
                this.gradualIntensity.minIntensity +
                (this.gradualIntensity.maxIntensity -
                    this.gradualIntensity.minIntensity) *
                    easedProgress;

            targetShapes.forEach(({ name, targetWeight }) => {
                const currentIntensity = targetWeight * baseIntensity;
                this.targetBlendShapes.set(name, currentIntensity);
            });

            if (progress >= 1.0) {
                clearInterval(interval);
                this.activePhonemes.delete(phoneme);
            }
        }, updateInterval);

        this.activePhonemes.set(phoneme, {
            interval,
            targetShapes,
            startTime: performance.now(),
        });
    }

    /**
     * Get target blendshape values for a phoneme without applying them
     * @param {string} phoneme - The phoneme to get targets for
     * @returns {Array} Array of {name, targetWeight} objects
     */
    getPhonemeTargetShapes(phoneme) {
        const availableBlendShapes = Object.keys(
            this.blendShapeControls,
        ).filter(
            (name) => !this.blendShapeMapper.isProtectedEmotionShape(name),
        );

        const targetShapes = [];

        // Get mapped shapes for all facial parts
        const mappedShapes = {
            head: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "head",
            ),
            mouth: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "mouth",
            ),
            beard: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "beard",
            ),
            moustache: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "moustache",
            ),
        };

        // Collect all target shapes
        Object.entries(mappedShapes).forEach(([part, shapes]) => {
            shapes.forEach(({ name, weight }) => {
                const control = this.blendShapeControls[name];
                if (
                    control &&
                    !this.blendShapeMapper.isProtectedEmotionShape(name)
                ) {
                    targetShapes.push({ name, targetWeight: weight });
                }
            });
        });

        return targetShapes;
    }

    /**
     * Easing function for smooth intensity transitions
     * @param {number} t - Progress value between 0 and 1
     * @returns {number} Eased value between 0 and 1
     */
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    applyPhoneme(phoneme) {
        // Handle MBP phoneme with 0 weight - no mouth movement
        if (phoneme === "MBP") {
            console.log(
                `Applying MBP phoneme: setting blend shapes to 0 weight`,
            );

            // Reset all non-emotion blend shapes to 0
            Object.keys(this.blendShapeControls).forEach((name) => {
                if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                    this.targetBlendShapes.set(name, 0);
                }
            });
            return;
        }

        // Handle pause phonemes differently - reset mouth to neutral position
        if (
            phoneme === "PAUSE_SHORT" ||
            phoneme === "PAUSE_MED" ||
            phoneme === "PAUSE_LONG"
        ) {
            console.log(`Applying pause phoneme: ${phoneme}`);

            // Reset all non-emotion blend shapes for pauses
            Object.keys(this.blendShapeControls).forEach((name) => {
                if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                    this.targetBlendShapes.set(name, 0);
                }
            });

            return;
        }

        // Regular phoneme processing for non-pause phonemes
        const availableBlendShapes = Object.keys(
            this.blendShapeControls,
        ).filter(
            (name) => !this.blendShapeMapper.isProtectedEmotionShape(name),
        );

        // Get mapped shapes
        const mappedShapes = {
            head: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "head",
            ),
            mouth: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "mouth",
            ),
            beard: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "beard",
            ),
            moustache: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                phoneme,
                "moustache",
            ),
        };

        // Reset all non-emotion blend shapes first
        Object.keys(this.blendShapeControls).forEach((name) => {
            if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                this.targetBlendShapes.set(name, 0);
            }
        });

        // Apply mapped shapes for each facial part
        Object.entries(mappedShapes).forEach(([part, shapes]) => {
            shapes.forEach(({ name, weight }) => {
                const control = this.blendShapeControls[name];
                if (
                    control &&
                    !this.blendShapeMapper.isProtectedEmotionShape(name)
                ) {
                    this.targetBlendShapes.set(name, weight);
                }
            });
        });
    }

    applyAnticipatedPhoneme(currentPhoneme, nextPhoneme, blendFactor = 0.3) {
        if (!nextPhoneme) return;

        // MBP phoneme always gets 0 weight - no blending for no mouth movement
        if (currentPhoneme === "MBP" || nextPhoneme === "MBP") {
            console.log(
                `MBP phoneme detected (current: ${currentPhoneme}, next: ${nextPhoneme}) - no anticipation blending, maintaining 0 weight`,
            );

            // Apply the appropriate phoneme
            if (nextPhoneme === "MBP") {
                this.applyPhoneme(nextPhoneme); // Apply MBP at 0 weight
            } else {
                this.applyPhoneme(currentPhoneme); // Maintain current phoneme if it's MBP
            }
            return;
        }

        // Special handling for pause phonemes
        const isPausePhoneme = (phoneme) =>
            ["PAUSE_SHORT", "PAUSE_MED", "PAUSE_LONG"].includes(phoneme);

        // If we're transitioning to a pause, gradually return to neutral
        if (isPausePhoneme(nextPhoneme)) {
            console.log(`Anticipating pause phoneme: ${nextPhoneme}`);

            // Gradually decrease mouth movements when approaching a pause
            Object.keys(this.blendShapeControls).forEach((name) => {
                if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                    const currentValue = this.targetBlendShapes.get(name) || 0;
                    // Reduce all blend shapes by the blend factor (moving toward neutral)
                    this.targetBlendShapes.set(
                        name,
                        currentValue * (1 - blendFactor),
                    );
                }
            });

            return;
        }

        // If we're coming out of a pause, don't apply anticipation
        // as we want a clean transition from silence to speech
        if (isPausePhoneme(currentPhoneme)) {
            return;
        }

        // Regular anticipation for normal phonemes
        const availableBlendShapes = Object.keys(
            this.blendShapeControls,
        ).filter(
            (name) => !this.blendShapeMapper.isProtectedEmotionShape(name),
        );

        // Get mapped shapes for both current and next phonemes
        const currentMappedShapes = {
            head: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                currentPhoneme,
                "head",
            ),
            mouth: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                currentPhoneme,
                "mouth",
            ),
            beard: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                currentPhoneme,
                "beard",
            ),
            moustache: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                currentPhoneme,
                "moustache",
            ),
        };

        const nextMappedShapes = {
            head: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                nextPhoneme,
                "head",
            ),
            mouth: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                nextPhoneme,
                "mouth",
            ),
            beard: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                nextPhoneme,
                "beard",
            ),
            moustache: this.blendShapeMapper.mapPhonemeToBlendShapes(
                availableBlendShapes,
                nextPhoneme,
                "moustache",
            ),
        };

        // Create a blended map of shape weights
        const blendedShapes = new Map();

        // Process current phoneme shapes with reduced weight
        Object.entries(currentMappedShapes).forEach(([part, shapes]) => {
            shapes.forEach(({ name, weight }) => {
                const control = this.blendShapeControls[name];
                if (
                    control &&
                    !this.blendShapeMapper.isProtectedEmotionShape(name)
                ) {
                    blendedShapes.set(name, weight * (1 - blendFactor));
                }
            });
        });

        // Blend with next phoneme shapes
        Object.entries(nextMappedShapes).forEach(([part, shapes]) => {
            shapes.forEach(({ name, weight }) => {
                const control = this.blendShapeControls[name];
                if (
                    control &&
                    !this.blendShapeMapper.isProtectedEmotionShape(name)
                ) {
                    const currentWeight = blendedShapes.get(name) || 0;
                    blendedShapes.set(
                        name,
                        currentWeight + weight * blendFactor,
                    );
                }
            });
        });

        // Apply the blended shapes
        blendedShapes.forEach((weight, name) => {
            this.targetBlendShapes.set(name, weight);
        });
    }

    _firstCtl(entry) {
        return Array.isArray(entry) ? entry[0] : entry;
    }

    _applyToAllTargets(entry, value) {
        const apply = (ctl) => {
            if (ctl && ctl.mesh && typeof ctl.index === "number") {
                ctl.mesh.morphTargetInfluences[ctl.index] = value;
                ctl.value = value;
            }
        };
        if (Array.isArray(entry)) {
            for (const c of entry) apply(c);
        } else {
            apply(entry);
        }
    }

    interpolateBlendShapes(delta) {
        this.targetBlendShapes.forEach((targetValue, name) => {
            const currentValue = this.currentBlendShapes.get(name) || 0;
            const control = this.blendShapeControls[name];
            const first = this._firstCtl(control);

            if (
                first &&
                first.mesh &&
                typeof first.index === "number" &&
                !this.blendShapeMapper.isProtectedEmotionShape(name)
            ) {
                const t = Math.min(1, delta / (this.transitionDuration / 1000));
                const newValue =
                    currentValue + (targetValue - currentValue) * t;

                this._applyToAllTargets(control, newValue);
                this.currentBlendShapes.set(name, newValue);
            }
        });
    }

    animate() {
        if (!this.isAnimating) return;

        const now = performance.now();
        const delta = (now - (this._lastFrame || now)) / 1000;
        this._lastFrame = now;

        this.interpolateBlendShapes(delta);

        requestAnimationFrame(() => this.animate());
    }

    /**
     * Set audio element for audio-based pause detection
     * @param {HTMLAudioElement} audioElement - The audio element to monitor
     * @param {Blob} audioBlob - Optional audio blob for analysis
     * @returns {Promise<Boolean>} - Resolves to true if analysis was successful
     */
    async setAudioSource(audioElement, audioBlob = null) {
        // Clear any existing audio source and analysis
        this.clearAudioSource();

        // Set the current audio element reference
        this.currentAudio = audioElement;

        // If we have an audio blob, analyze it for pauses
        if (audioBlob) {
            try {
                console.log("Analyzing audio blob for pauses...");
                this.detectedPauses =
                    await this.audioAnalyzer.analyzeFromBlob(audioBlob);
                this.usingAudioBasedPauses = this.detectedPauses.length > 0;

                console.log(
                    `Audio analysis complete. Detected ${this.detectedPauses.length} pauses.`,
                );

                // Start monitoring audio position if we're using audio-based pauses
                if (this.usingAudioBasedPauses) {
                    this.startAudioPauseMonitoring();
                    return true;
                }
            } catch (error) {
                console.error("Error analyzing audio for pauses:", error);
                this.usingAudioBasedPauses = false;
            }
        } else if (audioElement.src) {
            try {
                // If we don't have a blob but have a URL, analyze from URL
                console.log("Analyzing audio URL for pauses...");
                this.detectedPauses = await this.audioAnalyzer.analyzeFromUrl(
                    audioElement.src,
                );
                this.usingAudioBasedPauses = this.detectedPauses.length > 0;

                console.log(
                    `Audio URL analysis complete. Detected ${this.detectedPauses.length} pauses.`,
                );

                // Start monitoring audio position if we're using audio-based pauses
                if (this.usingAudioBasedPauses) {
                    this.startAudioPauseMonitoring();
                    return true;
                }
            } catch (error) {
                console.error("Error analyzing audio URL for pauses:", error);
                this.usingAudioBasedPauses = false;
            }
        }

        return false;
    }

    /**
     * Clear current audio source and pause monitoring
     */
    clearAudioSource() {
        // Stop monitoring audio position
        if (this.pauseUpdateInterval) {
            clearInterval(this.pauseUpdateInterval);
            this.pauseUpdateInterval = null;
        }

        // Clear audio references
        this.currentAudio = null;
        this.detectedPauses = [];
        this.usingAudioBasedPauses = false;

        // Reset pause tracking state
        this.lastAppliedPauseId = null;
        this.currentlyInPause = false;

        console.log("Audio source cleared and pause tracking reset");
    }

    /**
     * Start monitoring audio playback position to detect pauses
     */
    startAudioPauseMonitoring() {
        // Clear any existing interval
        if (this.pauseUpdateInterval) {
            clearInterval(this.pauseUpdateInterval);
        }

        // Reset pause tracking state
        this.lastAppliedPauseId = null;
        this.currentlyInPause = false;

        // Start a new interval to check audio position
        this.pauseUpdateInterval = setInterval(() => {
            if (
                !this.currentAudio ||
                !this.isAnimating ||
                !this.usingAudioBasedPauses
            ) {
                return;
            }

            // Get current audio playback time
            const currentTime = this.currentAudio.currentTime;

            // Check if we're in a pause
            const currentPause = this.audioAnalyzer.getPauseAtTime(currentTime);

            if (currentPause) {
                // Create a unique ID for this pause using start time as identifier
                const pauseId = `${currentPause.start.toFixed(3)}_${currentPause.type}`;

                // Only apply the pause when:
                // 1. We first enter a pause (currentlyInPause was false)
                // 2. OR we've entered a different pause than before (pauseId changed)
                if (
                    !this.currentlyInPause ||
                    this.lastAppliedPauseId !== pauseId
                ) {
                    // Apply the appropriate pause phoneme based on the pause type
                    this.applyPausePhoneme(currentPause.type);

                    // Log the pause for debugging
                    console.log(`Applying pause phoneme: ${currentPause.type}`);

                    // Update tracking state
                    this.lastAppliedPauseId = pauseId;
                    this.currentlyInPause = true;
                }

                // We're anticipating the next phoneme after this pause
                if (currentTime >= currentPause.end - 0.05) {
                    console.log(
                        `Anticipating end of pause: ${currentPause.type}`,
                    );
                }
            } else if (this.currentlyInPause) {
                // We've just exited a pause
                console.log(`Exited pause`);
                this.currentlyInPause = false;

                // Note: we keep the lastAppliedPauseId to prevent oscillation at pause boundaries
                // It will only be replaced when we detect a new valid pause
            }
        }, 50); // Check every 50ms (20 times per second)

        console.log(
            "Started audio-based pause monitoring with improved state tracking and jitter prevention",
        );
    }

    /**
     * Apply a pause phoneme type directly (used for audio-based pauses)
     * @param {string} pauseType - The type of pause to apply (PAUSE_SHORT, PAUSE_MED, PAUSE_LONG)
     */
    applyPausePhoneme(pauseType) {
        // Only handle recognized pause types
        if (!["PAUSE_SHORT", "PAUSE_MED", "PAUSE_LONG"].includes(pauseType)) {
            return;
        }

        console.log(`Applying pause phoneme from audio analysis: ${pauseType}`);

        // Reset all non-emotion blend shapes for pauses to zero
        // This creates a neutral mouth position during pauses
        Object.keys(this.blendShapeControls).forEach((name) => {
            if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                this.targetBlendShapes.set(name, 0);
            }
        });
    }

    stopAnimation() {
        this.isAnimating = false;

        // Clean up all active gradual intensity animations
        this.activePhonemes.forEach((phonemeData, phoneme) => {
            clearInterval(phonemeData.interval);
        });
        this.activePhonemes.clear();

        // Clear any audio monitoring
        this.clearAudioSource();

        this.resetBlendShapes();
        console.log(
            "Lip sync animation stopped, gradual intensities cleared, and blend shapes reset",
        );
    }

    /**
     * New method to handle timed phoneme sequences from ElevenLabs
     * Uses exact timestamps instead of predefined durations
     */
    startTimedLipSync(timedPhonemeData, audioElement = null) {
        if (
            !timedPhonemeData ||
            !timedPhonemeData.sequence ||
            timedPhonemeData.sequence.length === 0
        ) {
            console.warn("No timed phoneme data provided for lip sync");
            return;
        }

        console.log(
            `Starting timed lip sync with ${timedPhonemeData.phoneme_count} phonemes over ${timedPhonemeData.total_duration_ms}ms`,
        );

        this.isAnimating = true;
        this.currentAudio = audioElement;

        // Schedule each phoneme at its exact timestamp
        timedPhonemeData.sequence.forEach((phonemeData, index) => {
            setTimeout(() => {
                if (this.isAnimating) {
                    console.log(
                        `Applying timed phoneme: ${phonemeData.phoneme} at ${phonemeData.start_time_ms}ms for word "${phonemeData.word}"`,
                    );
                    this.applyPhoneme(phonemeData.phoneme);
                }
            }, phonemeData.start_time_ms);
        });

        // Schedule animation stop at the end
        setTimeout(() => {
            if (this.isAnimating) {
                console.log("Timed lip sync sequence completed");
                this.stopAnimation();
            }
        }, timedPhonemeData.total_duration_ms + this.endDelayMs);

        console.log(
            `Timed lip sync scheduled to complete at ${timedPhonemeData.total_duration_ms + this.endDelayMs}ms`,
        );
    }

    resetBlendShapes() {
        // Only clear non-emotion blend shapes
        [...this.targetBlendShapes.keys()].forEach((name) => {
            if (!this.blendShapeMapper.isProtectedEmotionShape(name)) {
                this.targetBlendShapes.delete(name);
                this.currentBlendShapes.delete(name);
            }
        });

        Object.entries(this.blendShapeControls).forEach(([name, control]) => {
            if (
                control.mesh &&
                typeof control.index === "number" &&
                !this.blendShapeMapper.isProtectedEmotionShape(name)
            ) {
                control.mesh.morphTargetInfluences[control.index] = 0;
                control.value = 0;
            }
        });
    }

    /**
     * Set up UI controls for lip sync anticipation settings
     */
    setupUI() {
        const debugPanel = document.getElementById("debug-panel");
        if (!debugPanel) {
            console.log("Debug panel not found, skipping lip sync UI setup");
            return;
        }

        const lipSyncControls = document.createElement("div");
        lipSyncControls.className = "mt-4";
        lipSyncControls.innerHTML = `
            <small class="text-muted">Lip Sync Controls:</small>
            <div class="mb-2">
                <label class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="anticipation-toggle" ${this.anticipation.enabled ? "checked" : ""}>
                    <span class="form-check-label">Enable Anticipation</span>
                </label>
            </div>
            <div class="mb-2">
                <label class="form-label small">Anticipation Factor</label>
                <input type="range" class="form-range" id="anticipation-factor"
                       min="0" max="1" step="0.05" value="${this.anticipation.factor}">
                <small class="text-muted" id="anticipation-factor-value">${this.anticipation.factor.toFixed(2)}</small>
            </div>
            <div class="mb-2">
                <label class="form-label small">Anticipation Timing</label>
                <input type="range" class="form-range" id="anticipation-timing"
                       min="0.1" max="0.9" step="0.05" value="${this.anticipation.offsetFactor}">
                <small class="text-muted" id="anticipation-timing-value">${this.anticipation.offsetFactor.toFixed(2)}</small>
            </div>
            <div class="mb-2">
                <label class="form-label small">Transition Duration (ms)</label>
                <input type="range" class="form-range" id="transition-duration"
                       min="50" max="300" step="10" value="${this.transitionDuration}">
                <small class="text-muted" id="transition-duration-value">${this.transitionDuration}ms</small>
            </div>
        `;

        debugPanel.appendChild(lipSyncControls);

        // Set up event listeners
        const anticipationToggle = document.getElementById(
            "anticipation-toggle",
        );
        const anticipationFactor = document.getElementById(
            "anticipation-factor",
        );
        const anticipationFactorValue = document.getElementById(
            "anticipation-factor-value",
        );
        const anticipationTiming = document.getElementById(
            "anticipation-timing",
        );
        const anticipationTimingValue = document.getElementById(
            "anticipation-timing-value",
        );
        const transitionDuration = document.getElementById(
            "transition-duration",
        );
        const transitionDurationValue = document.getElementById(
            "transition-duration-value",
        );

        if (anticipationToggle) {
            anticipationToggle.addEventListener("change", () => {
                this.setAnticipation({ enabled: anticipationToggle.checked });
            });
        }

        if (anticipationFactor && anticipationFactorValue) {
            anticipationFactor.addEventListener("input", () => {
                const value = parseFloat(anticipationFactor.value);
                anticipationFactorValue.textContent = value.toFixed(2);
                this.setAnticipation({ factor: value });
            });
        }

        if (anticipationTiming && anticipationTimingValue) {
            anticipationTiming.addEventListener("input", () => {
                const value = parseFloat(anticipationTiming.value);
                anticipationTimingValue.textContent = value.toFixed(2);
                this.setAnticipation({ offsetFactor: value });
            });
        }

        if (transitionDuration && transitionDurationValue) {
            transitionDuration.addEventListener("input", () => {
                const value = parseInt(transitionDuration.value);
                transitionDurationValue.textContent = `${value}ms`;
                this.transitionDuration = value;
            });
        }

        console.log("Lip sync UI controls initialized");
    }
}
